/* eslint-disable max-classes-per-file */

import { ProductTypeEnums } from "@/types/product/account/history";

import { ResponsePresenter } from "../../common/commonClassPresenter";
import { CorporateDisCountPresenter } from "../../corporate/corporateServiceInterfaces/corporateInterface";
import type { PerksBenefitPackage } from "../../flight/flightServiceInterfaces/getFlightFaresInterface";
import { AirportData } from "./getHistoryFlightListInterface";
import {
  ContactDatePresenter,
  DayPlanDataPresenter,
  ExpirePaymentDatePresenter,
  PaymentChannelDataPresenter,
  PaymentCouponDiscountPresenter,
  RefundedPaymentDatePresenter,
} from "./historyBookingCommonInterface";

export class FlightDetailDateTime {
  dateOri: string;

  date: string;

  year: string;

  time: string;

  constructor(response: any) {
    this.dateOri = response?.dateOri || "";
    this.date = response?.date || "";
    this.year = response?.year || "";
    this.time = response?.time || "";
  }
}

export class FlightDetailDuration {
  durationOri: string;

  hours: string;

  minutes: string;

  constructor(response: any) {
    this.durationOri = response?.durationOri || "";
    this.hours = response?.hours || "";
    this.minutes = response?.minutes || "";
  }
}

export class FlightDetailSegment {
  departure: string;

  arrival: string;

  constructor(response: any) {
    this.departure = response?.departure || "";
    this.arrival = response?.arrival || "";
  }
}

class FlightDetailFareSubDataPresenter {
  cityName: string;

  airportCode: string;

  airportName: string;

  countryName: string;

  dateTime: FlightDetailDateTime;

  constructor(response: any) {
    this.cityName = response?.cityName || "";
    this.airportCode = response?.airportCode || "";
    this.airportName = response?.airportName || "";
    this.countryName = response?.countryName || "";
    this.dateTime = new FlightDetailDateTime(response?.dateTime);
  }
}

class FlightDetailFareStopOverPresenter {
  cityName: string;

  airportCode: string;

  airportName: string;

  countryName: string;

  stopTime: number;

  duration: FlightDetailDuration;

  dateTime: FlightDetailDateTime;

  constructor(response: any) {
    this.cityName = response?.cityName || "";
    this.airportCode = response?.airportCode || "";
    this.airportName = response?.airportName || "";
    this.countryName = response?.countryName || "";
    this.stopTime = response?.stopTime || 0;
    this.duration = new FlightDetailDuration(response?.duration);
    this.dateTime = new FlightDetailDateTime(response?.dateTime);
  }
}

class FlightDetailFarePackagesPresenter {
  name: string;

  fareBase: string;

  numberOfStops: string;

  seatAvailable: string;

  freeMeal: boolean;

  freeMealDesc: string;

  seatSelection: boolean;

  deviceStreaming: boolean;

  wifi: boolean;

  powerOrUSBOutlets: boolean;

  recordSet: string;

  lowCostAirline: boolean;

  adultPrice: number;

  adultFare: number;

  adultTax: number;

  childPrice: number;

  childFare: number;

  childTax: number;

  infantPrice: number;

  infantFare: number;

  infantTax: number;

  bookingClass: string;

  equivalentBookingClass: string;

  adultOriginalPrice: number;

  childOriginalPrice: number;

  infantOriginalPrice: number;

  packageCurrency: string;

  baggageFree: boolean;

  benefits?: PerksBenefitPackage[];

  // flexiblePolicy: FlexiblePolicy
  // refundPolicy: RefundPolicy
  // iGoPack: string;
  // iGoPackKeys: any[];
  constructor(response: any) {
    this.name = response?.name || "";
    this.fareBase = response?.fareBase || "";
    this.numberOfStops = response?.numberOfStops || "";
    this.seatAvailable = response?.seatAvailable || "";
    this.freeMeal = response?.freeMeal || false;
    this.freeMealDesc = response?.freeMealDesc || "";
    this.seatSelection = response?.seatSelection || false;
    this.deviceStreaming = response?.deviceStreaming || false;
    this.wifi = response?.wifi || false;
    this.powerOrUSBOutlets = response?.powerOrUSBOutlets || false;
    this.recordSet = response?.recordSet || "";
    this.lowCostAirline = response?.lowCostAirline || false;
    this.adultPrice = response?.adultPrice || 0;
    this.adultFare = response?.adultFare || 0;
    this.adultTax = response?.adultTax || 0;
    this.childPrice = response?.childPrice || 0;
    this.childFare = response?.childFare || 0;
    this.childTax = response?.childTax || 0;
    this.infantPrice = response?.infantPrice || 0;
    this.infantFare = response?.infantFare || 0;
    this.infantTax = response?.infantTax || 0;
    this.bookingClass = response?.bookingClass || "";
    this.equivalentBookingClass = response?.equivalentBookingClass || "";
    this.adultOriginalPrice = response?.adultOriginalPrice || 0;
    this.childOriginalPrice = response?.childOriginalPrice || 0;
    this.infantOriginalPrice = response?.infantOriginalPrice || 0;
    this.packageCurrency = response?.packageCurrency || "";
    this.baggageFree = response?.baggageFree || false;
    this.benefits = response?.benefits || null;
  }
}

export class FlightDetailFareSegment {
  departure: string;

  departureAirportName: string;

  departureAirportCityName: string;

  departureAirportCountryName: string;

  departureCountryCode: string;

  arrival: string;

  arrivalAirportName: string;

  arrivalAirportCityName: string;

  arrivalAirportCountryName: string;

  arrivalCountryCode: string;

  duration: string;

  codeShare: string;

  operator: string;

  operatorName: string;

  majorCarrierCode: string;

  majorCarrierName: string;

  carrierCode: string;

  carrierName: string;

  carrierNameEn: string;

  equipmentCode: string;

  equipmentName: string;

  cabinClass: string;

  cabinClassName: string;

  techStop: string;

  techStopLocation: string;

  techStopArrivalDateTime: FlightDetailDateTime;

  techStopDepartureDateTime: FlightDetailDateTime;

  techStopDuration: FlightDetailDuration;

  techStopTime: number;

  techStopTimeUnit: string;

  techStopAirportName: string;

  techStopAirportCityName: string;

  techStopAirportCountryName: string;

  techStopCountryCode: string;

  flightNumber: string;

  flightTravelDuration: string;

  flightTravelTime: number;

  flightTravelTimeUnit: string;

  flightStopDuration: string;

  flightStopTime: number;

  flightStopTimeUnit: string;

  thumbnail: string;

  mileage: number;

  isGreenCertificate: boolean;

  isDomestic: boolean;

  travelTime: FlightDetailDuration;

  departureDateTime: FlightDetailDateTime;

  arrivalDateTime: FlightDetailDateTime;

  // greenCertifications: GreenCertification[]

  constructor(response: any) {
    this.departure = response?.departure || "";
    this.departureAirportName = response?.departureAirportName || "";
    this.departureAirportCityName = response?.departureAirportCityName || "";
    this.departureAirportCountryName =
      response?.departureAirportCountryName || "";
    this.departureCountryCode = response?.departureCountryCode || "";
    this.arrival = response?.arrival || "";
    this.arrivalAirportName = response?.arrivalAirportName || "";
    this.arrivalAirportCityName = response?.arrivalAirportCityName || "";
    this.arrivalAirportCountryName = response?.arrivalAirportCountryName || "";
    this.arrivalCountryCode = response?.arrivalCountryCode || "";
    this.duration = response?.duration || "";
    this.codeShare = response?.codeShare || "";
    this.operator = response?.operator || "";
    this.operatorName = response?.operatorName || "";
    this.majorCarrierCode = response?.majorCarrierCode || "";
    this.majorCarrierName = response?.majorCarrierName || "";
    this.carrierCode = response?.carrierCode || "";
    this.carrierName = response?.carrierName || "";
    this.carrierNameEn = response?.carrierNameEn || "";
    this.equipmentCode = response?.equipmentCode || "";
    this.equipmentName =
      response?.equipmentName &&
      response?.equipmentName?.toLowerCase() !== "n/a"
        ? response?.equipmentName
        : "";
    this.cabinClass = response?.cabinClass || "";
    this.cabinClassName = response?.cabinClassName || "";
    this.techStop = response?.techStop || "";
    this.techStopLocation = response?.techStopLocation || "";
    this.techStopArrivalDateTime = new FlightDetailDateTime(
      response?.techStopArrivalDateTime,
    );
    this.techStopDepartureDateTime = new FlightDetailDateTime(
      response?.techStopDepartureDateTime,
    );
    this.techStopDuration = new FlightDetailDuration(
      response?.techStopDuration,
    );
    this.techStopTime = response?.techStopTime || 0;
    this.techStopTimeUnit = response?.techStopTimeUnit || "";
    this.techStopAirportName = response?.techStopAirportName || "";
    this.techStopAirportCityName = response?.techStopAirportCityName || "";
    this.techStopAirportCountryName =
      response?.techStopAirportCountryName || "";
    this.techStopCountryCode = response?.techStopCountryCode || "";
    this.flightNumber = response?.flightNumber || "";
    this.flightTravelDuration = response?.flightTravelDuration || "";
    this.flightTravelTime = response?.flightTravelTime || 0;
    this.flightTravelTimeUnit = response?.flightTravelTimeUnit || "";
    this.flightStopDuration = response?.flightStopDuration || "";
    this.flightStopTime = response?.flightStopTime || 0;
    this.flightStopTimeUnit = response?.flightStopTimeUnit || "";
    this.thumbnail = response?.thumbnail || "";
    this.mileage = response?.mileage || 0;
    this.isGreenCertificate = response?.isGreenCertificate || "";
    this.isDomestic = response?.isDomestic || false;
    this.travelTime = new FlightDetailDuration(response?.travelTime);
    this.departureDateTime = new FlightDetailDateTime(
      response?.departureDateTime,
    );
    this.arrivalDateTime = new FlightDetailDateTime(response?.arrivalDateTime);
  }
}

export class FlightDetailFareDataPresenter {
  totalTimeUnit: number;

  IsDomestic: boolean;

  airlineIcon: string;

  pnr: string;

  dayPlan: DayPlanDataPresenter;

  duration: FlightDetailDuration;

  departure: FlightDetailFareSubDataPresenter;

  arrival: FlightDetailFareSubDataPresenter;

  stopOver: FlightDetailFareStopOverPresenter[];

  packages: FlightDetailFarePackagesPresenter;

  segments: FlightDetailFareSegment[];

  constructor(response: any) {
    this.totalTimeUnit = response?.totalTimeUnit || 0;
    this.IsDomestic = response?.IsDomestic || false;
    this.airlineIcon = response?.airlineIcon || "";
    this.dayPlan = new DayPlanDataPresenter(response?.dayPlan);
    this.duration = new FlightDetailDuration(response?.duration);
    this.departure = new FlightDetailFareSubDataPresenter(response?.departure);
    this.arrival = new FlightDetailFareSubDataPresenter(response?.arrival);
    this.stopOver = (response?.stopOver || []).map(
      (val: any) => new FlightDetailFareStopOverPresenter(val),
    );
    this.packages = new FlightDetailFarePackagesPresenter(response?.packages);
    this.segments = (response?.segments || []).map(
      (val: any) => new FlightDetailFareSegment(val),
    );
    this.pnr = response?.pnr || "";
  }
}

class FlightDetailFarePresenter {
  departure: FlightDetailFareDataPresenter;

  return: FlightDetailFareDataPresenter;

  constructor(response: any) {
    this.departure = new FlightDetailFareDataPresenter(response?.departure);
    this.return = new FlightDetailFareDataPresenter(response?.return);
  }
}

class FlightDetailAirlineMember {
  name: string;

  memberNumber: string;

  constructor(data: any) {
    this.name = data?.name || "";
    this.memberNumber = data?.memberNumber || "";
  }
}

export class FlightDetailPassenger {
  eTicket: string;

  airlineMember: FlightDetailAirlineMember[];

  constructor(response: any) {
    this.eTicket = response?.eTicket || "";
    this.airlineMember = (response?.airlineMember || []).map(
      (val: any) => new FlightDetailAirlineMember(val),
    );
  }
}

export class FlightDetailPassengerPresenter {
  titleName: string;

  firstName: string;

  middleName: string;

  lastName: string;

  birthday: string;

  nationality: string;

  countryNameOfPassport: string;

  cardType: string;

  cardId: string;

  cardExpired: string;

  departure: FlightDetailPassenger;

  return: FlightDetailPassenger;

  constructor(response: any) {
    this.titleName = response?.titleName || "";
    this.firstName = response?.firstName || "";
    this.middleName = response?.middleName || "";
    this.lastName = response?.lastName || "";
    this.birthday = response?.birthday || "";
    this.nationality = response?.nationality || "";
    this.countryNameOfPassport = response?.countryNameOfPassport || "";
    this.cardType = response?.cardType || "";
    this.cardId = response?.cardId || "";
    this.cardExpired = response?.cardExpired || "";
    this.departure = new FlightDetailPassenger(response?.departure);
    this.return = new FlightDetailPassenger(response?.return);
  }
}

class FlightDetailAddOnSeat {
  seatNumber: string;

  seatPrice: number;

  segment: FlightDetailSegment;

  isSeatList: boolean;

  constructor(response: any) {
    this.seatNumber = response?.seatNumber || "";
    this.seatPrice = response?.seatPrice || 0;
    this.segment = new FlightDetailSegment(response?.segment);
    this.isSeatList = response?.isSeatList || false;
  }
}
class FlightDetailAddOnMeal {
  mealCode: string;

  mealName: string;

  mealPrice: number;

  segment: FlightDetailSegment;

  isMealList: boolean;

  isFreeMeal: boolean;

  constructor(response: any) {
    this.segment = new FlightDetailSegment(response?.segment);
    this.mealCode = response?.mealCode || "";
    this.mealName = response?.mealName || "";
    this.mealPrice = response?.mealPrice || 0;
    this.isMealList = response?.isMealList || false;
    this.isFreeMeal = response?.isFreeMeal || false;
  }
}

export class FlightDetailAddOnBag {
  baggageName: string;

  baggagePrice: number;

  baggagePiece: number;

  baggageUnit: string;

  baggageTotalWeight: number;

  baggageConcept: string;

  constructor(response: any) {
    this.baggageName = response?.baggageName || "";
    this.baggagePrice = response?.baggagePrice || 0;
    this.baggagePiece = response?.baggagePiece || 0;
    this.baggageUnit = response?.baggageUnit || "";
    this.baggageTotalWeight = response?.baggageTotalWeight || 0;
    this.baggageConcept = response?.baggageConcept || "";
  }
}

class FlightDetailAddOnBaggageTracking {
  code: string;

  price: number;

  constructor(response: any) {
    this.code = response?.code || "";
    this.price = response?.price || 0;
  }
}

export class FlightDetailAddOnPassenger {
  passengerId: string;

  titleName: string;

  firstName: string;

  middleName: string;

  lastName: string;

  seats: FlightDetailAddOnSeat[];

  meals: FlightDetailAddOnMeal[];

  bagFree: FlightDetailAddOnBag;

  bagExtra: FlightDetailAddOnBag;

  baggageTracking: FlightDetailAddOnBaggageTracking;

  sportEquipment: FlightDetailAddOnBag;

  constructor(response: any) {
    this.passengerId = response?.passengerId || "";
    this.titleName = response?.titleName || "";
    this.firstName = response?.firstName || "";
    this.middleName = response?.middleName || "";
    this.lastName = response?.lastName || "";
    this.seats = (response?.seats || []).map(
      (val: any) => new FlightDetailAddOnSeat(val),
    );
    this.meals = (response?.meals || []).map(
      (val: any) => new FlightDetailAddOnMeal(val),
    );
    this.bagFree = new FlightDetailAddOnBag(response?.bagFree);
    this.bagExtra = new FlightDetailAddOnBag(response?.bagExtra);
    this.baggageTracking = new FlightDetailAddOnBaggageTracking(
      response?.baggageTracking,
    );
    this.sportEquipment = new FlightDetailAddOnBag(response?.sportEquipment);
  }
}

class FlightDetailAddOnFare {
  icon: string;

  airport: {
    departure: AirportData;
    arrival: AirportData;
  };

  flightStop: number;

  constructor(response: any) {
    this.icon = response?.icon || "";
    this.flightStop = response?.flightStop || 0;
    this.airport = {
      departure: new AirportData(response?.airport?.departure),
      arrival: new AirportData(response?.airport?.arrival),
    };
  }
}

export class FlightDetailAddOnDataPresenter {
  fare: FlightDetailAddOnFare;

  passengers: FlightDetailAddOnPassenger[];

  constructor(response: any) {
    this.fare = new FlightDetailAddOnFare(response?.fare);
    this.passengers = (response?.passengers || []).map(
      (val: any) => new FlightDetailAddOnPassenger(val),
    );
  }
}

class FlightDetailAddOnPresenter {
  departure: FlightDetailAddOnDataPresenter;

  return: FlightDetailAddOnDataPresenter;

  constructor(response: any) {
    this.departure = new FlightDetailAddOnDataPresenter(response?.departure);
    this.return = new FlightDetailAddOnDataPresenter(response?.return);
  }
}

class PricePassenger {
  fare: number;

  tax: number;

  amount: number;

  constructor(response: any) {
    this.fare = response?.fare || 0;
    this.tax = response?.tax || 0;
    this.amount = response?.amount || 0;
  }
}

class FlightDetailPaymentPricePassenger {
  adult: PricePassenger;

  child: PricePassenger;

  infant: PricePassenger;

  total: number;

  constructor(response: any) {
    this.adult = new PricePassenger(response?.adult);
    this.child = new PricePassenger(response?.child);
    this.infant = new PricePassenger(response?.infant);
    this.total = response?.total || 0;
  }
}

class BagExtraList {
  name: string;

  count: number;

  pricePerPiece: number;

  total: number;

  constructor(response: any) {
    this.name = response?.name || "";
    this.count = response?.count || 0;
    this.pricePerPiece = response?.pricePerPiece || 0;
    this.total = response?.total || 0;
  }
}

class BagExtraPrice {
  total: number;

  lists: BagExtraList[];

  constructor(response: any) {
    this.total = response?.total || 0;
    this.lists = (response?.lists || []).map(
      (val: any) => new BagExtraList(val),
    );
  }
}

class FlightDetailPaymentPriceAddOn {
  bagExtra: BagExtraPrice;

  meal: BagExtraPrice;

  seat: BagExtraPrice;

  baggageTracking: BagExtraPrice;

  sportEquipment: BagExtraPrice;

  constructor(response: any) {
    this.bagExtra = new BagExtraPrice(response?.bagExtra);
    this.meal = new BagExtraPrice(response?.meal);
    this.seat = new BagExtraPrice(response?.seat);
    this.baggageTracking = new BagExtraPrice(response?.baggageTracking);
    this.sportEquipment = new BagExtraPrice(response?.sportEquipment);
  }
}

class FlightDetailPaymentPay {
  passengers: {
    adult: number;
    child: number;
    infant: number;
  };

  pricePassenger: FlightDetailPaymentPricePassenger;

  priceAddOn: FlightDetailPaymentPriceAddOn;

  constructor(response: any) {
    this.passengers = {
      adult: response?.passengers?.adult || 0,
      child: response?.passengers?.child || 0,
      infant: response?.passengers?.infant || 0,
    };
    this.pricePassenger = new FlightDetailPaymentPricePassenger(
      response?.pricePassenger,
    );
    this.priceAddOn = new FlightDetailPaymentPriceAddOn(response?.priceAddOn);
  }
}

class FlightDetailPaymentPresenter {
  paymentType: string;

  paymentUrl: string;

  isPaymentExpire: boolean;

  isAllocatesExpire: boolean;

  expireAllocateDate: ExpirePaymentDatePresenter;

  expirePaymentDate: ExpirePaymentDatePresenter;

  channel: PaymentChannelDataPresenter;

  couponDiscount: PaymentCouponDiscountPresenter;

  priceIncrease: {
    amount: number;
    currency: string;
  };

  pay: FlightDetailPaymentPay;

  paymentSuccessDate: string;

  bookingDate: string;

  refunded: RefundedPaymentDatePresenter;

  constructor(response: any) {
    this.paymentType = response?.paymentType || "";
    this.paymentUrl = response?.paymentUrl || "";
    this.isPaymentExpire = response?.isPaymentExpire || false;
    this.isAllocatesExpire = response?.isAllocatesExpire || false;
    this.expireAllocateDate = new ExpirePaymentDatePresenter(
      response?.expireAllocateDate,
    );
    this.expirePaymentDate = new ExpirePaymentDatePresenter(
      response?.expirePaymentDate,
    );
    this.channel = new PaymentChannelDataPresenter(response?.channel);
    this.couponDiscount = new PaymentCouponDiscountPresenter(
      response?.couponDiscount,
    );
    this.priceIncrease = {
      amount: response?.priceIncrease?.amount || 0,
      currency: response?.priceIncrease?.currency || "",
    };
    this.pay = new FlightDetailPaymentPay(response?.pay);
    this.bookingDate = response?.bookingDate || "";
    this.paymentSuccessDate = response?.paymentSuccessDate || "";
    this.refunded = new RefundedPaymentDatePresenter(response?.refunded);
  }
}

class FlightDetailFlexiblePolicy {
  flexible: boolean;

  price: number;

  chargeType: string;

  maxChange: number;

  desc: string;

  constructor(response: any) {
    this.flexible = response?.flexible || false;
    this.price = response?.price || 0;
    this.chargeType = response?.chargeType || "";
    this.maxChange = response?.maxChange || 0;
    this.desc = (response?.desc || "").toLocaleLowerCase();
  }
}

class FlightDetailRefundPolicy {
  refundable: boolean;

  refundFee: number;

  refundDesc: string;

  constructor(response: any) {
    this.refundable = response?.refundable || false;
    this.refundFee = response?.refundFee || 0;
    this.refundDesc = (response?.refundDesc || "").toLocaleLowerCase();
  }
}

export class FlightDetailPolicyFare {
  flexiblePolicy: FlightDetailFlexiblePolicy;

  refundPolicy: FlightDetailRefundPolicy;

  constructor(response: any) {
    this.flexiblePolicy = new FlightDetailFlexiblePolicy(
      response?.flexiblePolicy,
    );
    this.refundPolicy = new FlightDetailRefundPolicy(response?.refundPolicy);
  }
}

class FlightDetailPolicyPresenter {
  departure: FlightDetailPolicyFare;

  return: FlightDetailPolicyFare;

  constructor(response: any) {
    this.departure = new FlightDetailPolicyFare(response?.departure);
    this.return = new FlightDetailPolicyFare(response?.return);
  }
}

class FlightDetailDataPresenter extends CorporateDisCountPresenter {
  productType: string;

  bookingInternalNo: string;

  pinCode: string;

  bookingDate: string;

  price: number;

  currency: string;

  routeType: string;

  typeOfTrip: string;

  bookingStatus: string;

  paymentStatus: string;

  bookingStep: string;

  buttonCancelStatus: boolean;

  buttonResendPayment: boolean;

  buttonResendVoucher: boolean;

  dateCancel: string;

  refundStatus: string;

  fare: FlightDetailFarePresenter;

  contact: ContactDatePresenter;

  passengers: FlightDetailPassengerPresenter[];

  addOn: FlightDetailAddOnPresenter;

  payment: FlightDetailPaymentPresenter;

  policy: FlightDetailPolicyPresenter;

  type: string;

  constructor(response: any) {
    super(response);
    this.productType = response?.productType || ProductTypeEnums.FLIGHT;
    this.bookingInternalNo = response?.bookingInternalNo || "";
    this.pinCode = response?.pinCode || "";
    this.bookingDate = response?.bookingDate || "";
    this.price = response?.price || 0;
    this.currency = response?.currency || "";
    this.routeType = response?.routeType || "";
    this.typeOfTrip = (response?.typeOfTrip || "").toLocaleLowerCase();
    this.bookingStatus = (response?.bookingStatus || "").toLocaleLowerCase();
    this.paymentStatus = (response?.paymentStatus || "").toLocaleLowerCase();
    this.bookingStep = (response?.bookingStep || "").toLocaleLowerCase();
    this.buttonCancelStatus = response?.buttonCancelStatus || false;
    this.buttonResendPayment = response?.buttonResendPayment || false;
    this.buttonResendVoucher = response?.buttonResendVoucher || false;
    this.dateCancel = response?.dateCancel || "";
    this.refundStatus = response?.refundStatus || "";
    this.fare = new FlightDetailFarePresenter(response?.fare);
    this.contact = new ContactDatePresenter(response?.contact);
    this.passengers = (response?.passengers || []).map(
      (val: any) => new FlightDetailPassengerPresenter(val),
    );
    this.addOn = new FlightDetailAddOnPresenter(response?.addOn);
    this.payment = new FlightDetailPaymentPresenter(response?.payment);
    this.policy = new FlightDetailPolicyPresenter(response?.policy);
    this.type = response?.type || ProductTypeEnums.FLIGHT;
  }
}

export class ResponseGetDetailFlightPresenter extends ResponsePresenter<FlightDetailDataPresenter> {
  cToken: string;

  constructor(response: any) {
    super(response, (data) => new FlightDetailDataPresenter(data));
    this.cToken = response?.cToken || "";
  }
}
